import { useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLanguage } from "../../../contexts/LanguageContext";
import { getNavConfigByRole } from "./navConfig";
import { useUserLayoutState } from "./useUserLayoutState";

/** Callbacks et données dérivées du layout utilisateur. */
export function useUserLayout(onLogout, user) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const { isCollapsed, setIsCollapsed, isMobileMenuOpen, setIsMobileMenuOpen } =
    useUserLayoutState();

  const isAdminUser =
    String(user?.role || "").toLowerCase() === "admin" ||
    Boolean(user?.is_admin || user?.isAdmin);
  const effectiveRole = isAdminUser ? "admin" : user?.role;
  const menuTitle = isAdminUser ? "Menu Admin" : "Menu";

  const handleLogout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("sidebarCollapsed");
    onLogout?.(null, null);
    navigate("/");
  }, [navigate, onLogout]);

  const handleNavigation = useCallback(
    (path) => {
      navigate(path);
      setIsMobileMenuOpen(false);
    },
    [navigate, setIsMobileMenuOpen],
  );

  const navItems = useMemo(
    () =>
      getNavConfigByRole(effectiveRole).map(({ id, icon, path, tKey, fallback }) => {
        const translatedLabel = tKey ? t(tKey) : "";
        return {
          id,
          icon,
          path,
          label: translatedLabel || fallback,
        };
      }),
    [t, effectiveRole],
  );

  const isActive = useCallback(
    (itemPath) => {
      const normalizePath = (path) => {
        if (!path) return "/";
        return path.length > 1 ? path.replace(/\/+$/, "") : path;
      };

      const currentPath = normalizePath(location.pathname);
      const targetPath = normalizePath(itemPath);

      return (
        currentPath === targetPath || currentPath.startsWith(`${targetPath}/`)
      );
    },
    [location.pathname],
  );

  return {
    isCollapsed,
    setIsCollapsed,
    isMobileMenuOpen,
    setIsMobileMenuOpen,
    handleLogout,
    handleNavigation,
    navItems,
    menuTitle,
    isActive,
    t,
  };
}
